import { useEffect, useState } from "react"
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { api_endpoint } from "@/constants/constant"
import axios from "axios"
import { useAuth } from "@clerk/clerk-react"
import { Spinner } from "@/components/spinner"
import { toast } from "sonner"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../@/components/ui/select";
import Papa from "papaparse";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";

function EnrollStudentsToPathway({
    courseId,
    onEnrolled,
    enrolledStudentIds,
    triggerLabel = "Enroll Students",
    triggerVariant = "default",
    triggerClassName = "",
}) {
    const [open, setOpen] = useState(false)
    const [allStudents, setAllStudents] = useState([])
    const [selectedIds, setSelectedIds] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [allPathways, setAllPathways] = useState([])
    const [selectedPathway, setSelectedPathway] = useState("")
    const [enrolledUserIdsForPathway, setEnrolledUserIdsForPathway] = useState([]);
    const { getToken } = useAuth()

    useEffect(() => {
        if (open) {
            // Fetch all students
            async function fetchAllStudents() {
                setLoading(true)
                try {
                    const token = await getToken()
                    const reqConfig = {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        }
                    }
                    const users = await fetchAllPaginatedItems({
                        url: api_endpoint + `/user/users`,
                        token,
                        selectItems: (data) => data?.users ?? [],
                        mode: "skip",
                        pageSize: 200,
                        headers: reqConfig.headers,
                    })
                    setAllStudents(users || [])

                } catch (error) {
                    console.error("Error fetching students:", error)
                    toast.error("Error fetching students")
                    setAllStudents([])
                } finally {
                    setLoading(false)
                }
            }
            fetchAllStudents()

            async function fetchAllPathways() {
                try {
                    const response = await axios.get(api_endpoint + "/pathway/")
                    setAllPathways(response.data?.pathways || [])
                } catch (error) {
                    console.error("Error fetching pathways:", error)
                    toast.error("Error fetching pathways")
                    setAllPathways([])
                }
            }
            fetchAllPathways()
        }
    }, [open, getToken])


    // Fetch enrolled students for selected pathway
    useEffect(() => {
        async function fetchEnrolledForPathway() {
            if (!selectedPathway) {
                setEnrolledUserIdsForPathway([]);
                return;
            }
            setLoading(true);
            try {
                const token = await getToken();
                const reqConfig = {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                };
                const response = await axios.get(api_endpoint + `/pathway/enrollments/${selectedPathway}`, reqConfig);
                const enrolled = response.data?.enrollments || [];
                setEnrolledUserIdsForPathway(enrolled.map(e => e.user_id));
            } catch (error) {
                setEnrolledUserIdsForPathway([]);
            } finally {
                setLoading(false);
            }
        }
        if (selectedPathway) fetchEnrolledForPathway();
    }, [selectedPathway, getToken]);

    // Filter out already enrolled students
    // const notEnrolledStudents = allStudents.filter(student => !enrolledStudentIds.includes(student.id))

    // Filter students: not already enrolled in selected pathway
    const filteredStudents = allStudents.filter(student => {
        const name = `${student.first_name ?? ""} ${student.last_name ?? ""}`.toLowerCase()
        const email = (student.email ?? "").toLowerCase()
        const term = searchTerm.toLowerCase()
        const notEnrolled = !enrolledUserIdsForPathway.includes(student.id);
        return notEnrolled && (name.includes(term) || email.includes(term));
    });

    const handleSelect = (id) => {
        setSelectedIds((prev) => {
            return prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
        })
    }

    // Bulk select from CSV
    const handleCsvUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: function (results) {
                const users = results.data;
                const emails = users.map(u =>
                    (u.email || u.Email || u.EMAIL || u.EMAILS || u.emails || u.Emails)?.trim().toLowerCase()
                ).filter(Boolean);

                // Emails in your system
                const allEmails = allStudents.map(s => s.email?.toLowerCase());
                // Emails not already enrolled in this pathway
                const notEnrolledEmails = allStudents
                    .filter(student => !enrolledUserIdsForPathway.includes(student.id))
                    .map(student => student.email?.toLowerCase());

                // Emails not found
                const notFound = emails.filter(email => !allEmails.includes(email));
                // Emails already enrolled
                const alreadyEnrolled = emails.filter(email => !notEnrolledEmails.includes(email) && allEmails.includes(email));

                // Match emails to user IDs in notEnrolledStudents
                const matchedIds = allStudents
                    .filter(student =>
                        emails.includes(student.email?.toLowerCase()) &&
                        !enrolledUserIdsForPathway.includes(student.id)
                    )
                    .map(student => student.id);

                setSelectedIds(matchedIds);

                let message = `${matchedIds.length} students selected from CSV.`;
                if (notFound.length > 0) message += ` ${notFound.length} emails not found.`;
                if (alreadyEnrolled.length > 0) message += ` ${alreadyEnrolled.length} emails already enrolled.`;

                toast.success(message);
            },
            error: function (error) {
                toast.error("Failed to parse CSV");
                console.error(error);
            }
        });
    };

    const handleEnroll = async () => {
        setLoading(true)
        try {
            const token = await getToken()
            await axios.post(
                api_endpoint + "/pathway/batch-enroll",
                {
                    pathway_id: Number(selectedPathway),
                    user_ids: selectedIds
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            )
            if (onEnrolled) onEnrolled()
            setOpen(false)
            toast.success("Students enrolled successfully to pathway")
        } catch (error) {
            console.error("Error enrolling students to pathway:", error)
            toast.error("Error enrolling students to pathway")
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant={triggerVariant}
                    className={`w-full sm:w-auto rounded-md ${triggerClassName}`.trim()}
                >
                    {triggerLabel}
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading text-(--neutral-900)">Enroll Students</DialogTitle>
                    <DialogDescription className="text-sm text-(--neutral-700) font-body">
                        Select students to enroll in a pathway. Use the search box to find students by name or email. <br />
                    </DialogDescription>
                </DialogHeader>
                <div className="mb-3">
                    <Select value={selectedPathway} onValueChange={setSelectedPathway}>
                        <SelectTrigger className="w-full mb-2">
                            <SelectValue placeholder="Select Pathway" className="font-body" />
                        </SelectTrigger>
                        <SelectContent>
                            {allPathways.map(pathway => (
                                <SelectItem key={pathway.id} value={String(pathway.id)} className="font-body">
                                    {pathway.title}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Input
                        placeholder="Search students"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full font-body"
                    />
                    {/* <Input
                        type="file"
                        accept=".csv"
                        onChange={handleCsvUpload}
                        className="w-full font-body mt-2"
                    />
                    <span className="text-xs text-(--neutral-600) font-body">
                        You can also upload a CSV file with a column named <b>email</b> to bulk select students.
                    </span> */}
                </div>
                {loading ? (
                    <Spinner />
                ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2">
                        {filteredStudents.length === 0 ? (
                            <div className="text-sm text-(--neutral-700) font-body">
                                {searchTerm
                                    ? "Student not found"
                                    : "No students available for enrollment"}
                            </div>
                        ) : (
                            filteredStudents.map((student) => (
                                <div key={student.id} className="flex items-center gap-2 border-b border-(--neutral-300) py-2">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(student.id)}
                                        onChange={() => handleSelect(student.id)}
                                        className="w-4 h-4"
                                    />
                                    <span className="font-body text-(--neutral-900)">{student.first_name} {student.last_name} ({student.email})</span>
                                </div>
                            )))}
                    </div>
                )}

                <DialogFooter className="">
                        <Button
                            variant="outline"
                            onClick={() => setOpen(false)}
                            disabled={loading}
                            className="">
                            Cancel
                        </Button>
                        <Button
                            variant=""
                            onClick={handleEnroll}
                            disabled={loading || selectedIds.length === 0 || !selectedPathway}
                            className="">
                            Enroll Selected
                        </Button>
                </DialogFooter>

            </DialogContent>
        </Dialog>
    )
}

export default EnrollStudentsToPathway